import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import type { IProduct, IProductCustomerMaterial, ITheme } from '@inter-ikea-kompis/types';
import { ProductUtility } from '@inter-ikea-kompis/utilities';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import {
	Accordion,
	AccordionItem,
	AccordionItemPadding
} from '@inter-ikea-kompis/component-accordion';
import { Divider } from '@inter-ikea-kompis/component-divider';
import {
	WoodDeclaration,
	WoodDeclarationCard
} from '@inter-ikea-kompis/component-wood-declaration';

import { Text } from '@ingka/common-styles-webc';
import { ProductIdentifier as SkapaProductIdentifier } from '@ingka/product-identifier-webc/passive.js';

import ProductInformationExpandedAccordionEnum from '../../enums/ProductInformationExpandedAccordionEnum.js';
import ProductInformationSectionSecurityAndComplianceEnum from '../../enums/ProductInformationSectionSecurityAndComplianceEnum.js';
import type IProductInformationSectionLocalizedInformation from '../../types/IProductInformationSectionLocalizedInformation.js';
import type IProductInformationSectionTranslations from '../../types/IProductInformationSectionTranslations.js';
import ProductDetailsCardStyle from './ProductDetailsCardStyle.js';

// Manufacturer, Importer & Country of Origin
const COUNTRY_OF_ORIGIN_CODES = ['00719', '00759'];
const WOOD_DECLARATION_CODE = '00692';

/**
 * This is the Product details card sub component to Product information section.
 */

export default class ProductDetailsCard extends KompisElement {
	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Localized information. Deprecated - use "translations" instead.
	@property({ type: Object })
	public localizedInformation: IProductInformationSectionLocalizedInformation | null = null;

	// The translations retrieved from translations service.
	@property({ type: Object })
	public translations: IProductInformationSectionTranslations | null = null;

	// A product with the type IProduct.
	@property({ type: Object, required: true })
	public product!: IProduct;

	private expandedAccordionIndex: ProductInformationExpandedAccordionEnum | null = null;

	/**
	 * Renders the component.
	 *
	 * @returns Template.
	 */
	public override render(): RenderResult {
		// We want translations to be required, but since both props to provide translations are optional, this check is needed until localizedInformation is removed.
		if (!this.hasTranslations()) {
			// We can't throw an error because on the first render no props are set.
			return null;
		}

		const headingTextClass = Text.classnames({ heading: { m: true } });

		return html`
			<style>
				${ProductDetailsCardStyle.getStyle({ theme: this.theme })}
			</style>

			<div class="container">
				<h2 class="header ${headingTextClass}">
					${this.getTranslations().productCardProductDetailsTitle}
				</h2>

				${this.getCustomerBenefits()}${this.getDistanceSellingInformationRequirements()}${this.getArticleNumber()}

				<kompis-accordion .theme="${this.theme}">
					${this.getGoodToKnowAccordionItem()} ${this.getMaterialAndCareAccordionItem()}
					${this.getSafetyAndComplianceAccordionItem()}
				</kompis-accordion>
			</div>
		`;
	}

	public override async connectedCallback(): Promise<void> {
		this.customElements.define('skapa-product-identifier', SkapaProductIdentifier);
		this.customElements.define('kompis-accordion', Accordion);
		this.customElements.define('kompis-accordion-item', AccordionItem);
		this.customElements.define('kompis-accordion-item-padding', AccordionItemPadding);
		this.customElements.define('kompis-wood-declaration-card', WoodDeclarationCard);
		this.customElements.define('kompis-divider', Divider);
		this.customElements.define('kompis-wood-declaration', WoodDeclaration);

		super.connectedCallback();
	}

	private hasTranslations(): boolean {
		return !!this.translations || !!this.localizedInformation?.translations;
	}

	private getTranslations(): IProductInformationSectionTranslations {
		return this.translations
			? this.translations
			: <IProductInformationSectionTranslations>this.localizedInformation?.translations;
	}

	private getGoodToKnowAccordionItem(): RenderResult {
		const goodToKnow = this.product.content?.goodToKnow;

		if (!goodToKnow) {
			return null;
		}

		const filteredGoodToKnow = goodToKnow.filter(
			(item) =>
				!Object.values(ProductInformationSectionSecurityAndComplianceEnum).includes(
					<ProductInformationSectionSecurityAndComplianceEnum>item.typeName
				)
		);

		if (!filteredGoodToKnow || filteredGoodToKnow.length === 0) {
			return null;
		}

		const { productDetailsGoodToKnowHeader } = this.getTranslations();

		return html`
			<kompis-accordion-item
				id="goodToKnow"
				.noHorizontalPadding="${true}"
				@expand="${(event: CustomEvent) => {
					this.expandedAccordionIndex = event.detail.expanded
						? ProductInformationExpandedAccordionEnum.goodToKnow
						: null;
					this.requestUpdate();
				}}"
				.expanded="${this.expandedAccordionIndex ===
				ProductInformationExpandedAccordionEnum.goodToKnow}"
				.theme="${this.theme}"
				.label="${productDetailsGoodToKnowHeader}"
			>
				<kompis-accordion-item-padding>
					<section class="goodToKnow">
						${filteredGoodToKnow.map(({ text }) => this.renderBodyText(text))}
					</section>
				</kompis-accordion-item-padding>
			</kompis-accordion-item>
		`;
	}

	/**
	 *	The "textType" property occurs several times, this removes the duplicates to form a rendered group.
	 *
	 * @param customerMaterial IProductCustomerMaterial[].
	 * @returns CustomerMaterial[].
	 */
	private removeDuplicateTextTypes(
		customerMaterial: IProductCustomerMaterial[]
	): IProductCustomerMaterial[] {
		return customerMaterial.reduce(
			(acc: IProductCustomerMaterial[], curr: IProductCustomerMaterial) => {
				const textTypeExists = acc.some((product) => product.textType === curr.textType);
				if (textTypeExists) {
					const { textType, ...materialItem } = curr;
					acc.push(materialItem as IProductCustomerMaterial);
				} else {
					acc.push(curr);
				}

				return acc;
			},
			[]
		);
	}

	private getTextType(textType: string | null): RenderResult {
		if (!textType) {
			return null;
		}

		const headingTextClass = Text.classnames({ heading: { xs: true } });
		return html`<h4 class="textTypeSubHeading ${headingTextClass}">${textType}</h4>`;
	}

	private productMaterialHasTextType(product: IProduct): boolean {
		if (!product.content?.customerMaterial) {
			return false;
		}

		return product.content.customerMaterial.some((material) => material.textType);
	}

	private getMaterialAndCareAccordionItem(): RenderResult {
		const customerMaterial = this.product.content?.customerMaterial;
		const careInstruction = this.product.content?.careInstruction;
		const woodDeclaration = this.product?.content?.complianceInformation?.find(
			(compliance) => compliance.datasetCode === WOOD_DECLARATION_CODE
		);

		if (!careInstruction && !customerMaterial && !woodDeclaration) {
			return null;
		}

		const {
			productDetailsMaterialAndCareCareSubheader,
			productDetailsMaterialAndCareMaterialSubheader,
			productDetailsMaterialAndCareInstructionsHeader
		} = this.getTranslations();

		let customerMaterialHTML: RenderResult = null;
		let careInstructionHTML: RenderResult = null;
		let woodDeclarationHTML: RenderResult = null;

		const headingTextClass = Text.classnames({ heading: { xs: true } });

		if (customerMaterial) {
			const withTextTypeHeader = this.productMaterialHasTextType(this.product)
				? this.removeDuplicateTextTypes(customerMaterial)
				: customerMaterial;

			customerMaterialHTML = html`
				<section class="customerMaterial">
					<h3 class="materialAndCareHeader ${headingTextClass}">
						${productDetailsMaterialAndCareMaterialSubheader}
					</h3>
					${withTextTypeHeader.map(({ text, textPart, textType = null }) => {
						return html`${this.getTextType(textType)} ${this.renderBodyText(text, textPart)}`;
					})}
				</section>
			`;
		}

		if (woodDeclaration) {
			woodDeclarationHTML = html`
				${customerMaterial && html`<kompis-divider .theme="${this.theme}"></kompis-divider>`}
				<kompis-wood-declaration-card
					.theme="${this.theme}"
					.product="${this.product}"
					.showHeader="${true}"
				>
				</kompis-wood-declaration-card>
			`;
		}

		if (careInstruction) {
			careInstructionHTML = html`
				${(woodDeclarationHTML || customerMaterialHTML) &&
				html`<kompis-divider .theme="${this.theme}"></kompis-divider>`}
				<section class="careInstruction">
					<h3 class="${headingTextClass}">${productDetailsMaterialAndCareCareSubheader}</h3>
					${careInstruction.map(({ text }) => this.renderBodyText(text))}
				</section>
			`;
		}

		return html`
			<kompis-accordion-item
				id="materialsAndCare"
				.noHorizontalPadding="${true}"
				@expand="${(event: CustomEvent) => {
					this.expandedAccordionIndex = event.detail.expanded
						? ProductInformationExpandedAccordionEnum.materialsAndCare
						: null;
					this.requestUpdate();
				}}"
				.expanded="${this.expandedAccordionIndex ===
				ProductInformationExpandedAccordionEnum.materialsAndCare}"
				.theme="${this.theme}"
				.label="${productDetailsMaterialAndCareInstructionsHeader}"
			>
				<kompis-accordion-item-padding>
					<section class="materialsAndCare">
						${customerMaterialHTML} ${woodDeclarationHTML} ${careInstructionHTML}
					</section>
				</kompis-accordion-item-padding>
			</kompis-accordion-item>
		`;
	}

	/**
	 * The safety and compliance is filtered out from the good to know section with the help of
	 * the ProductInformationSectionSecurityAndComplianceEnum.
	 */
	private getSafetyAndComplianceAccordionItem(): RenderResult {
		const safetyAndCompliance = this.product.content?.goodToKnow?.filter((item) =>
			Object.values(ProductInformationSectionSecurityAndComplianceEnum).includes(
				<ProductInformationSectionSecurityAndComplianceEnum>item.typeName
			)
		);

		if (!safetyAndCompliance || safetyAndCompliance.length === 0) {
			return null;
		}

		const sortOrder = Object.values(ProductInformationSectionSecurityAndComplianceEnum);
		const sortedSafteyAndCompliance = safetyAndCompliance.sort(
			(a, b) =>
				sortOrder.indexOf(<ProductInformationSectionSecurityAndComplianceEnum>a.typeName) -
				sortOrder.indexOf(<ProductInformationSectionSecurityAndComplianceEnum>b.typeName)
		);

		const { productDetailsSafetyAndComplianceHeader } = this.getTranslations();

		return html`
			<kompis-accordion-item
				id="safetyAndCompliance"
				.noHorizontalPadding="${true}"
				@expand="${(event: CustomEvent) => {
					this.expandedAccordionIndex = event.detail.expanded
						? ProductInformationExpandedAccordionEnum.safetyAndCompliance
						: null;
					this.requestUpdate();
				}}"
				.expanded="${this.expandedAccordionIndex ===
				ProductInformationExpandedAccordionEnum.safetyAndCompliance}"
				.theme="${this.theme}"
				.label="${productDetailsSafetyAndComplianceHeader}"
			>
				<kompis-accordion-item-padding>
					<section class="safetyAndCompliance">
						${sortedSafteyAndCompliance.map(({ text }) => this.renderBodyText(text))}
					</section>
				</kompis-accordion-item-padding>
			</kompis-accordion-item>
		`;
	}

	private renderBodyText(text: string, textPart?: string): RenderResult {
		const bodyTextClass = Text.classnames({ body: { m: true } });
		const headingTextClass = Text.classnames({ heading: { xs: true } });

		return html`
			${textPart ? html`<span class="textPart ${headingTextClass}">${textPart}</span>` : null}
			<span class="${bodyTextClass}">${text}</span>
		`;
	}

	private getCustomerBenefits(): RenderResult {
		const customerBenefit = this.product?.content?.customerBenefit;

		if (!customerBenefit) {
			return null;
		}

		const sortedCustomerBenefits = [...customerBenefit].sort((a, b) => {
			return a.sortNo - b.sortNo;
		});

		return html`
			<section class="customerBenefits">
				${sortedCustomerBenefits.map(({ text }) => this.renderBodyText(text))}
			</section>
		`;
	}

	private getArticleNumber(): RenderResult {
		if (!this.product?.content?.ruItemNo) {
			return null;
		}

		return html`
			<skapa-product-identifier label="${this.getTranslations().productArticleNo}">
				${ProductUtility.formatItemNo(this.product?.content?.ruItemNo)}
			</skapa-product-identifier>
		`;
	}

	private getDistanceSellingInformationRequirements(): RenderResult[] | null {
		if (!this.product.content?.complianceInformation) {
			return null;
		}

		return this.product.content.complianceInformation.map((complianceInformation) => {
			if (COUNTRY_OF_ORIGIN_CODES.includes(complianceInformation.datasetCode)) {
				const { headingText, text } = complianceInformation;
				const headingTextClass = Text.classnames({ heading: { xs: true } });

				// TODO - this modifies all compliance texts. I.e. IKEA becomes Ikea
				// Capitalize compliance text.
				const capitalizedComplianceText = text
					.toLowerCase()
					.replace(/\b[a-z]/g, (firstLetter) => firstLetter.toUpperCase())
					.replace(/,/g, ', ');

				return html`
					<section class="distanceSelling">
						<h3 class="distanceSellingHeader ${headingTextClass}">${headingText}</h3>

						${this.renderBodyText(capitalizedComplianceText)}
					</section>
				`;
			} else {
				return null;
			}
		});
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'skapa-product-identifier': SkapaProductIdentifier;
		'kompis-product-details-card': ProductDetailsCard;
	}
}
